import { Button, Card, Col, Form, Row } from "antd";
import FullPageLayout from "../../../../templates/FullPageLayout";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { InputField, InputPhone } from "../../../../atoms/FormFields";
import { useLocation, useNavigate } from "react-router-dom";
import { NeedHelp, UploadIcon } from "../../../../atoms/CustomIcons";
import { AddressComponent, Place } from "../../../Company/ducks/types";
import { AutoCompleteField } from "../../../../atoms/FormFields/AutoComplete";
import ErrorMessage from "../../../../atoms/Toasts/ErrorMessage";
import { AxiosError } from "axios";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import { createCrewYard } from "../ducks/services";

interface FormValues {
  name: string;
  address: string;
  crews_assigned: string;
  cont_person_first_name: string;
  cont_person_last_name: string;
  cont_person_email: string;
  cont_person_phone: string;
}

export default function AddCrewYards() {
  const {
    control,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<FormValues>();
  const url = useLocation();
  const companyId = url?.pathname.split("/")[3];
  const [loading, setLoading] = useState(false);
  const [googleAddress, setGoogleAddress] = useState<Place | null>(null);
  const navigate = useNavigate();

  const onSubmit = (val: FormValues) => {
    let city = "";
    let state = "";
    googleAddress?.address_components?.forEach(
      (component: AddressComponent) => {
        if (component.types.includes("locality")) {
          city = component.long_name;
        }
        if (component.types.includes("administrative_area_level_1")) {
          state = component.short_name;
        }
      }
    );

    setLoading(true);
    const payload = {
      name: val.name,
      address: val?.address,
      city: city,
      state: state,
      cont_person_first_name: val?.cont_person_first_name
        ? val?.cont_person_first_name
        : undefined,
      cont_person_last_name: val?.cont_person_last_name
        ? val?.cont_person_last_name
        : undefined,
      cont_person_email: val?.cont_person_email
        ? val?.cont_person_email
        : undefined,
      cont_person_phone: val?.cont_person_phone
        ? val?.cont_person_phone
        : undefined,
      // crews_assigned: val?.crews_assigned,
      company_id: companyId,
    };

    createCrewYard(payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Crew yard added.");
        navigate(`/company-settings/crew-yards/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  const saveAddMore = async () => {
    const allValues = getValues();
    const isValid = await trigger();

    if (isValid) {
      let city = "";
      let state = "";
      googleAddress?.address_components?.forEach(
        (component: AddressComponent) => {
          if (component.types.includes("locality")) {
            city = component.long_name;
          }
          if (component.types.includes("administrative_area_level_1")) {
            state = component.short_name;
          }
        }
      );

      setLoading(true);
      const payload = {
        name: allValues.name,
        address: allValues?.address,
        city: city,
        state: state,
        cont_person_first_name: allValues?.cont_person_first_name
          ? allValues?.cont_person_first_name
          : undefined,
        cont_person_last_name: allValues?.cont_person_last_name
          ? allValues?.cont_person_last_name
          : undefined,
        cont_person_email: allValues?.cont_person_email
          ? allValues?.cont_person_email
          : undefined,
        cont_person_phone: allValues?.cont_person_phone
          ? allValues?.cont_person_phone
          : undefined,
        // crews_assigned: val?.crews_assigned,
        company_id: companyId,
      };

      createCrewYard(payload)
        .then(() => {
          setLoading(false);
          SuccessMessage("Crew yard added.");
          window.location.reload();
          setGoogleAddress(null);
        })
        .catch((e: unknown) => {
          if (e instanceof AxiosError) {
            setLoading(false);
            ErrorMessage(
              e?.response?.data?.error?.message ?? "Something went wrong"
            );
          } else {
            setLoading(false);
            ErrorMessage("Something went wrong");
          }
        });
    }
  };

  return (
    <FullPageLayout loading={loading}>
      <Button className="btn-sm fixedTopBtn">
        <NeedHelp /> Need help?
      </Button>
      <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
        <Col style={{ width: "672px", paddingBottom: 72 }}>
          <div
            className="title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2> Add crew yard </h2>
            <Button
              htmlType="button"
              className="outlined-btn btn-sm"
              type="primary"
              onClick={() =>
                navigate(`/company-settings/upload-crew-yard/${companyId}`)
              }
            >
              <UploadIcon /> Bulk upload
            </Button>
          </div>
          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <h4 style={{ marginBottom: "16px" }}> Crew yard details </h4>
              </Col>
              <Col span={24}>
                <InputField
                  fieldName="name"
                  label="Yard name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter yard name",
                  }}
                  isRequired
                  rules={{
                    required: "Yard name is required.",
                  }}
                  validate={errors.name && "error"}
                  validMessage={errors.name && errors.name.message}
                />
              </Col>
              <Col span={24} className="mb-0">
                <AutoCompleteField
                  fieldName="address"
                  label="Address"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter address",
                  }}
                  onPlaceChange={(place: Place) => setGoogleAddress(place)}
                  isRequired
                  rules={{
                    required: "Address is required.",
                  }}
                  validate={errors.address && "error"}
                  validMessage={errors.address && errors.address.message}
                />
                {/* <SelectField
                  fieldName="crews_assigned"
                  label="Crews assigned"
                  control={control}
                  selectOption={[]}
                  iProps={{
                    placeholder: "Select Crews assigned",
                    mode: "tags",
                  }}
                  isRequired
                  rules={{
                    required: "Crews assigned is required.",
                  }}
                  validate={errors.crews_assigned && "error"}
                  validMessage={
                    errors.crews_assigned && errors.crews_assigned.message
                  }
                  tooltipTitle="The crew yard will be suggested as a default starting and ending location for these crews."
                /> */}
              </Col>
            </Row>
          </Card>

          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <h4 style={{ marginBottom: "16px" }}> Contact person </h4>
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="cont_person_first_name"
                  label="First name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter first name",
                  }}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="cont_person_last_name"
                  label="Last name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter last name",
                  }}
                />
              </Col>
              <Col span={12} className="mb-0">
                <InputField
                  fieldName="cont_person_email"
                  label="Email"
                  control={control}
                  defaultValue=""
                  className="requiredInput"
                  iProps={{
                    placeholder: "Enter email",
                  }}
                  rules={{
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Incorrect email address.",
                    },
                  }}
                  validate={errors.cont_person_email && "error"}
                  validMessage={
                    errors.cont_person_email && errors.cont_person_email.message
                  }
                />
              </Col>
              <Col span={12} className="mb-0">
                <InputPhone
                  fieldName="cont_person_phone"
                  label="Phone"
                  control={control}
                />
              </Col>
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() =>
                  navigate(`/company-settings/crew-yards/${companyId}`)
                }
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  htmlType="button"
                  className="outlined-btn"
                  type="primary"
                  onClick={saveAddMore}
                >
                  Save & add more
                </Button>
                <Button
                  htmlType="submit"
                  type="primary"
                  className="mint-btn"
                  // disabled={true}
                >
                  Save & close
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
